import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/shadcn/combobox';
import { Field, FieldError, FieldLabel } from '@/components/shadcn/field';

type Props<T extends FieldValues> = {
  control: Control<T>;
  employees:
    | {
        label: string;
        value: string;
      }[]
    | undefined;
  name?: Path<T>;
  required?: boolean;
};

export default function AssigneeField<T extends FieldValues>({
  employees,
  control,
  name,
  required,
}: Props<T>) {
  const { t } = useTranslation();

  const fieldName = name ?? ('assignee' as Path<T>);

  return (
    <Controller
      control={control}
      name={fieldName}
      rules={{
        required: required ? t('PARTY_CREATE_INLINE_REQUIRED') : false,
      }}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.error ? true : undefined}>
          <FieldLabel htmlFor="assignee">
            {t('TASK_FIELD_ASSIGNED_TO')}
            {required && ' *'}
          </FieldLabel>

          <Combobox
            items={employees ?? []}
            value={field.value ?? null}
            onValueChange={field.onChange}
            name={field.name}
          >
            <ComboboxInput placeholder={t('TASK_FIELD_ASSIGNED_TO_PH')} />

            <ComboboxContent>
              <ComboboxList>
                {(item: NonNullable<typeof employees>[number]) => (
                  <ComboboxItem key={item.value} value={item}>
                    {item.label}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>

          <FieldError
            errors={fieldState.error ? [fieldState.error] : undefined}
          />
        </Field>
      )}
    />
  );
}
